import { useMemo } from 'react';
import { useCommandCenter, selectFilteredAssets } from '../store';
import type { AssetDto } from '../api';

const STATUSES: AssetDto['status'][] = [
  'ACTIVE',
  'IDLE',
  'ALERT',
  'OFFLINE',
  'STALE',
  'MAINTENANCE',
];

export function LeftRail() {
  const search = useCommandCenter((s) => s.search);
  const setSearch = useCommandCenter((s) => s.setSearch);
  const statusFilter = useCommandCenter((s) => s.statusFilter);
  const toggleStatus = useCommandCenter((s) => s.toggleStatusFilter);
  const select = useCommandCenter((s) => s.select);
  const selectedId = useCommandCenter((s) => s.selectedAssetId);
  const assets = useCommandCenter(selectFilteredAssets);

  const grouped = useMemo(() => {
    const buckets: Record<string, AssetDto[]> = {};
    for (const a of assets) {
      (buckets[a.status] ??= []).push(a);
    }
    return buckets;
  }, [assets]);

  return (
    <aside className="flex h-full flex-col border-r border-cc-border bg-cc-surface text-cc-fg">
      <div className="border-b border-cc-border p-3">
        <label className="block text-[10px] font-semibold uppercase tracking-wider text-cc-muted">
          Assets · {assets.length}
        </label>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, code, plate…"
          className="mt-1 w-full rounded-none border border-cc-border bg-cc-bg px-2 py-1 font-mono text-xs text-cc-fg outline-none focus:border-cc-accent"
        />
        <div className="mt-2 flex flex-wrap gap-1">
          {STATUSES.map((s) => {
            const active = statusFilter.has(s);
            return (
              <button
                key={s}
                onClick={() => toggleStatus(s)}
                className={`rounded-none border px-1.5 py-0.5 font-mono text-[10px] uppercase ${
                  active
                    ? 'border-cc-accent bg-cc-accent/20 text-cc-accent'
                    : 'border-cc-border bg-cc-bg text-cc-muted hover:text-cc-fg'
                }`}
              >
                {s.toLowerCase()}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {assets.length === 0 ? (
          <p className="p-4 font-mono text-xs text-cc-muted">[ NO ASSETS MATCH ]</p>
        ) : (
          STATUSES.filter((s) => grouped[s]?.length).map((status) => (
            <div key={status}>
              <div className="sticky top-0 z-10 border-b border-cc-border bg-cc-surface px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-cc-muted">
                {status} · {grouped[status].length}
              </div>
              {grouped[status].map((asset) => (
                <button
                  key={asset.id}
                  onClick={() => select(asset.id)}
                  className={`flex w-full items-center justify-between border-l-2 px-3 py-2 text-left font-mono text-xs hover:bg-cc-bg ${
                    selectedId === asset.id ? 'border-cc-accent bg-cc-bg' : 'border-transparent'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <StatusDot status={asset.status} />
                    <span className="text-cc-fg">{asset.name}</span>
                  </span>
                  <span className="text-cc-muted">
                    {asset.speed_kmh != null ? `${asset.speed_kmh.toFixed(0)} km/h` : '—'}
                  </span>
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </aside>
  );
}

export function StatusDot({ status }: { status: AssetDto['status'] }) {
  const color =
    status === 'ACTIVE'
      ? 'bg-cc-ok'
      : status === 'IDLE'
        ? 'bg-cc-warn'
        : status === 'ALERT'
          ? 'bg-cc-crit'
          : status === 'STALE'
            ? 'bg-cc-muted opacity-60'
            : 'bg-cc-muted';
  return <span className={`inline-block h-2 w-2 rounded-full ${color}`} />;
}
